/** Original procedural characters and props. Coordinates use x across, y up and
 * positive z into the course; all resources belong to the caller's disposable set. */
export function bobMeshFactory(THREE, { prepareMaterial = (material) => material } = {}) {
  const resources = new Set();
  const keep = (resource) => (resources.add(resource), resource);
  const sphere = keep(new THREE.SphereGeometry(1, 24, 16));
  const cube = keep(new THREE.BoxGeometry(1, 1, 1));
  const cylinder = keep(new THREE.CylinderGeometry(1, 1, 1, 32));
  const ring = keep(new THREE.TorusGeometry(1, 0.075, 8, 40));
  const cone = keep(new THREE.ConeGeometry(1, 1, 12));
  const detailSphere = keep(new THREE.SphereGeometry(1, 12, 8));
  const prism = keep(new THREE.CylinderGeometry(1, 1, 1, 6));
  const crystalTip = keep(new THREE.ConeGeometry(1, 1, 6));
  const blockOutline = new THREE.Shape();
  blockOutline.moveTo(-0.42, -0.42);
  blockOutline.lineTo(0.42, -0.42);
  blockOutline.lineTo(0.42, 0.42);
  blockOutline.lineTo(-0.42, 0.42);
  blockOutline.closePath();
  const softCube = keep(
    new THREE.ExtrudeGeometry(blockOutline, {
      depth: 0.84,
      steps: 1,
      bevelEnabled: true,
      bevelSize: 0.08,
      bevelThickness: 0.08,
      bevelSegments: 3,
    }),
  );
  softCube.translate(0, 0, -0.42);
  const roofBase = new THREE.BufferGeometry();
  roofBase.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(
      [-0.5, 0, -0.5, 0.5, 0, -0.5, 0, 1, -0.5, -0.5, 0, 0.5, 0.5, 0, 0.5, 0, 1, 0.5],
      3,
    ),
  );
  roofBase.setIndex([0, 2, 1, 3, 4, 5, 0, 3, 5, 0, 5, 2, 1, 2, 5, 1, 5, 4, 0, 1, 4, 0, 4, 3]);
  const roof = keep(roofBase.toNonIndexed());
  roofBase.dispose();
  roof.computeVertexNormals();
  const materials = new Map();
  function material(colour, glow = 0, transparent = false, scenic = false) {
    const key = `${colour}:${glow}:${transparent}:${scenic}`;
    if (!materials.has(key))
      materials.set(
        key,
        keep(
          new THREE.MeshPhysicalMaterial({
            color: colour,
            roughness: scenic ? 0.48 : 0.3,
            metalness: scenic ? 0.015 : 0.08,
            clearcoat: scenic ? 0.36 : 0.9,
            clearcoatRoughness: scenic ? 0.3 : 0.18,
            emissive: colour,
            emissiveIntensity: glow,
            transparent,
            opacity: transparent ? 0.48 : 1,
          }),
        ),
      );
    return prepareMaterial(materials.get(key));
  }
  function shape(parent, geometry, colour, position, scale, glow = 0) {
    const mesh = new THREE.Mesh(
      geometry,
      material(colour, glow, false, !!parent.userData.bobScenery),
    );
    mesh.position.set(...position);
    mesh.scale.set(...scale);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  }
  const ball = (p, c, xyz, scale, glow) => shape(p, sphere, c, xyz, scale, glow);
  const box = (p, c, xyz, scale, glow) => shape(p, cube, c, xyz, scale, glow);
  const softBox = (p, c, xyz, scale) => shape(p, softCube, c, xyz, scale);
  const bead = (p, c, xyz, scale) => shape(p, detailSphere, c, xyz, scale);
  function torus(parent, colour, xyz, radius, glow = 0) {
    return shape(parent, ring, colour, xyz, [radius, radius, radius], glow);
  }
  function character() {
    const root = new THREE.Group();
    root.name = 'Bob — original full-body character';
    const body = new THREE.Group();
    root.add(body);
    // The rig faces local -Z; the motion owner turns it toward course travel.
    ball(body, '#ff664d', [0, 0.05, 0], [0.49, 0.51, 0.34]);
    ball(body, '#148ca9', [0, -0.2, -0.035], [0.45, 0.3, 0.33]);
    box(body, '#21bad0', [0, 0.05, -0.316], [0.37, 0.33, 0.06]);
    for (const side of [-1, 1]) {
      box(body, '#21bad0', [side * 0.17, 0.25, -0.27], [0.065, 0.28, 0.075]);
      ball(body, '#ffe478', [side * 0.17, 0.08, -0.36], [0.04, 0.04, 0.024]);
    }
    const head = new THREE.Group();
    head.position.y = 0.61;
    body.add(head);
    ball(head, '#ffd2a3', [0, 0, -0.005], [0.37, 0.36, 0.315]);
    ball(head, '#653b29', [0, 0.205, 0.025], [0.365, 0.205, 0.29]);
    const cap = new THREE.Group();
    head.add(cap);
    ball(cap, '#ffce35', [0, 0.265, -0.015], [0.39, 0.16, 0.31]);
    ball(cap, '#ffad29', [0, 0.225, -0.29], [0.32, 0.05, 0.19]);
    const eyes = [],
      brows = [];
    ball(head, '#ffbd91', [0, -0.02, -0.32], [0.1, 0.09, 0.11]);
    for (const side of [-1, 1]) {
      ball(head, '#ffd2a3', [side * 0.355, -0.025, 0], [0.08, 0.11, 0.07]);
      const eye = new THREE.Group();
      eye.position.set(side * 0.14, 0.035, 0);
      head.add(eye);
      ball(eye, '#ffffff', [0, 0, -0.284], [0.086, 0.105, 0.042]);
      ball(eye, '#223953', [0, -0.01, -0.324], [0.043, 0.062, 0.025]);
      ball(eye, '#ffffff', [-0.012, 0.011, -0.348], [0.014, 0.02, 0.01]);
      eyes.push(eye);
      ball(head, '#ff9d8d', [side * 0.245, -0.095, -0.25], [0.075, 0.045, 0.025]);
      const brow = ball(head, '#653b29', [side * 0.145, 0.162, -0.272], [0.083, 0.02, 0.02]);
      brow.rotation.z = side * 0.1;
      brows.push(brow);
    }
    const smile = torus(head, '#773d36', [0, -0.105, -0.294], 0.105);
    smile.scale.y = 0.063;
    const smileMask = ball(head, '#ffd2a3', [0, -0.055, -0.315], [0.12, 0.06, 0.03]);
    smileMask.castShadow = false;
    const mouthOpen = new THREE.Group();
    mouthOpen.name = 'Bob surprised mouth';
    ball(mouthOpen, '#733a44', [0, -0.12, -0.31], [0.076, 0.09, 0.027]);
    box(mouthOpen, '#fff7e8', [0, -0.071, -0.335], [0.085, 0.021, 0.014]);
    mouthOpen.visible = false;
    head.add(mouthOpen);
    const arms = [],
      legs = [];
    for (const side of [-1, 1]) {
      const arm = new THREE.Group();
      arm.position.set(side * 0.4, 0.22, 0);
      body.add(arm);
      ball(arm, '#ff664d', [side * 0.07, -0.045, 0], [0.14, 0.19, 0.14]);
      ball(arm, '#ffd2a3', [side * 0.12, -0.24, -0.02], [0.1, 0.17, 0.1]);
      ball(arm, '#ffd2a3', [side * 0.12, -0.38, -0.02], [0.115, 0.12, 0.105]);
      arms.push(arm);
      const leg = new THREE.Group();
      leg.position.set(side * 0.2, -0.34, 0);
      body.add(leg);
      ball(leg, '#148ca9', [0, -0.13, 0], [0.15, 0.24, 0.16]);
      ball(leg, '#fff8e5', [0, -0.31, -0.08], [0.18, 0.1, 0.24]);
      ball(leg, '#ffce35', [0, -0.275, -0.1], [0.155, 0.06, 0.215]);
      legs.push(leg);
    }
    return { root, body, head, arms, legs, cap, eyes, brows, smile, mouthOpen };
  }
  function platform(data, colour) {
    const group = new THREE.Group();
    const w = data.width,
      d = data.depth;
    box(group, colour, [0, -0.16, 0], [w, 0.3, d]);
    box(group, '#fff7db', [0, 0.025, 0], [w * 0.97, 0.065, d * 0.97]);
    box(group, colour, [0, 0.07, 0], [w * 0.86, 0.03, d * 0.86]);
    for (const side of [-1, 1]) {
      box(group, '#ffee74', [side * w * 0.45, -0.13, 0], [0.055, 0.14, d * 0.78], 0.2);
      ball(group, colour, [side * w * 0.38, -0.23, d * 0.35], [0.15, 0.22, 0.15]);
    }
    return group;
  }
  function food(kind, index) {
    const group = new THREE.Group();
    if (kind === 'banana') {
      for (let i = 0; i < 5; i++) {
        const a = (i / 4 - 0.5) * 1.7;
        ball(group, '#ffd83d', [Math.sin(a) * 0.3, -Math.cos(a) * 0.3 + 0.1, 0], [0.12, 0.1, 0.1]);
      }
      ball(group, '#875332', [-0.25, -0.07, 0], [0.045, 0.045, 0.045]);
      ball(group, '#875332', [0.25, -0.07, 0], [0.045, 0.045, 0.045]);
    } else if (kind === 'cookie') {
      const biscuit = shape(group, cylinder, '#e8ad59', [0, 0, 0], [0.3, 0.13, 0.3]);
      biscuit.rotation.x = Math.PI / 2;
      for (let i = 0; i < 7; i++) {
        const a = i * 2.4;
        ball(
          group,
          '#79452d',
          [Math.sin(a) * 0.2, Math.cos(a) * 0.2, -0.075],
          [0.035, 0.035, 0.02],
        );
      }
    } else if (kind === 'croissant') {
      for (let i = 0; i < 5; i++) {
        const a = (i / 4 - 0.5) * 2.4;
        const pastry = ball(
          group,
          i % 2 ? '#f5c06c' : '#dc9540',
          [Math.sin(a) * 0.25, Math.cos(a) * 0.15, 0],
          [0.115, 0.16 - Math.abs(i - 2) * 0.035, 0.13],
        );
        pastry.rotation.z = -a;
      }
    } else if (kind === 'berry') {
      for (let i = 0; i < 7; i++) {
        const a = i * 2.4;
        ball(
          group,
          '#ce56b5',
          [Math.sin(a) * 0.13, Math.cos(a) * 0.13, (i % 2) * 0.07],
          [0.125, 0.125, 0.125],
        );
      }
      ball(group, '#6bdb74', [0, 0.26, 0], [0.17, 0.04, 0.08]);
    } else if (/donut|dough|ring/.test(kind)) {
      const pastry = torus(group, '#db8737', [0, 0, 0], 0.25);
      pastry.scale.set(1, 1, 1.6);
      const icing = torus(group, '#ff69b4', [0, 0, -0.03], 0.25);
      icing.scale.set(1, 1, 1.2);
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3;
        box(
          group,
          i % 2 ? '#5ae5fc' : '#fff096',
          [Math.cos(a) * 0.25, Math.sin(a) * 0.25, -0.115],
          [0.035, 0.07, 0.035],
        );
      }
    } else {
      const colour = ['#ff4f63', '#ffbb32', '#b573ff', '#76df4f'][index % 4];
      ball(group, colour, [0, 0, 0], [0.24, 0.26, 0.24]);
      box(group, '#885129', [0, 0.28, 0], [0.045, 0.14, 0.045]);
      const leaf = ball(group, '#5acf63', [0.1, 0.29, 0], [0.13, 0.04, 0.065]);
      leaf.rotation.z = 0.4;
      ball(group, '#ffffff', [-0.085, 0.08, -0.205], [0.045, 0.085, 0.014]);
    }
    return group;
  }
  function hazard(kind, radius, behavior = 'hazard') {
    const group = new THREE.Group();
    if (kind === 'box' && behavior === 'bumper') {
      box(group, '#31b6c9', [0, 0, 0], [0.86, 0.86, 0.86]);
      for (const x of [-0.33, 0.33])
        for (const y of [-0.33, 0.33])
          for (const z of [-0.33, 0.33]) ball(group, '#54d3d4', [x, y, z], [0.17, 0.17, 0.17]);
      for (const z of [-0.46, 0.46]) {
        ball(group, '#ffdd76', [0, 0, z], [0.31, 0.3, 0.045]);
        box(group, '#fff6d5', [0, 0, z * 1.07], [0.09, 0.32, 0.025]);
        box(group, '#fff6d5', [0, 0, z * 1.07], [0.29, 0.075, 0.025]);
      }
    } else if (kind === 'box') {
      box(group, '#793551', [0, 0, 0], [0.78, 0.78, 0.78]);
      for (let axis = 0; axis < 3; axis++)
        for (const side of [-1, 1]) {
          const position = [0, 0, 0];
          position[axis] = side * 0.39;
          const spike = shape(group, cone, '#ff557b', position, [0.19, 0.22, 0.19]);
          if (axis === 0) spike.rotation.z = (-side * Math.PI) / 2;
          if (axis === 1 && side < 0) spike.rotation.z = Math.PI;
          if (axis === 2) spike.rotation.x = (side * Math.PI) / 2;
        }
      const warning = box(group, '#ffeaa2', [0, 0.15, -0.405], [0.22, 0.22, 0.035]);
      warning.rotation.z = Math.PI / 4;
    } else if (/wind/.test(kind)) {
      for (let i = 0; i < 3; i++)
        torus(group, '#b9faff', [0, i * 0.2, 0], radius * (0.7 + i * 0.15));
    } else {
      ball(group, '#682761', [0, 0, 0], [radius, radius, radius]);
      for (let i = 0; i < 8; i++) {
        const spike = shape(
          group,
          keep(new THREE.ConeGeometry(radius * 0.24, radius * 0.75, 8)),
          '#ff6785',
          [0, 0, 0],
          [1, 1, 1],
        );
        const a = (i * Math.PI) / 4;
        spike.position.set(Math.sin(a) * radius, Math.cos(a) * radius, 0);
        spike.rotation.z = -a;
      }
      box(group, '#fff4d6', [0, 0, -radius], [radius * 1.2, radius * 0.2, 0.04]);
    }
    return group;
  }
  function finish(width = 4) {
    const group = new THREE.Group();
    group.name = 'Finish crossing arch';
    // The inner markers match the canonical capture width, and the arch clears
    // the whole airborne route instead of suggesting a floor landing target.
    for (const side of [-1, 1]) {
      shape(group, cylinder, '#ffe17b', [side * (width / 2 + 0.09), 4, 0], [0.09, 8, 0.09]);
      ball(group, '#ff6dad', [side * (width / 2 + 0.09), 8.1, 0], [0.2, 0.2, 0.2], 0.3);
    }
    for (let x = 0; x < 8; x++)
      for (let y = 0; y < 2; y++)
        box(
          group,
          (x + y) % 2 ? '#ffffff' : '#384469',
          [-width / 2 + (width * (x + 0.5)) / 8, 7.5 + y * 0.23, 0],
          [width / 8, 0.23, 0.07],
        );
    box(group, '#fff4ab', [0, 0.035, 0], [width, 0.035, 0.25], 0.5);
    const halo = torus(group, '#ffe34b', [0, 4, 0], width / 2, 0.8);
    return { group, halo };
  }
  function scenery(theme, index, palette) {
    const group = new THREE.Group();
    group.name = `scenery:${theme}:${index}`;
    // Painted scenery keeps richer colour under the game lights than the
    // glossy reward material. Small ornaments reuse a lower-detail sphere.
    group.userData.bobScenery = true;
    const h = 3.8 + (index % 3) * 1.1;
    if (theme.includes('city') || theme === 'celestial-palace') {
      const frost = theme === 'frost-city',
        neon = theme === 'neon-city',
        palace = theme === 'celestial-palace';
      const walls = frost
        ? ['#83cfe6', '#abb6f5', '#d6b7ed']
        : neon
          ? ['#394778', '#7555a2', '#b15d9e']
          : palace
            ? ['#ffd9b1', '#eed3ff', '#8fdad6']
            : ['#fa826a', '#43c5c7', '#ffc067', '#ef87b7'];
      const wall = walls[index % walls.length];
      const trim = '#fff2d7',
        accent = ['#167f99', '#b75385', '#de793e'][index % 3];
      const facadeHeight = h + (index % 2) * 1.5;
      softBox(group, wall, [0, facadeHeight / 2, 0], [5.2, facadeHeight, 3.4]);
      softBox(group, trim, [0, 0.2, 0], [5.38, 0.4, 3.55]);
      softBox(group, trim, [0, facadeHeight - 0.2, 0], [5.45, 0.2, 3.65]);
      box(group, trim, [0, 2.1, -1.74], [5.35, 0.13, 0.14]);
      const roofColour = frost
        ? '#edf9ff'
        : neon
          ? '#454e8c'
          : palace
            ? '#e9b33e'
            : ['#db5670', '#218ca0', '#9b60b8'][index % 3];
      if (index % 3 === 0 || theme === 'celestial-palace') {
        softBox(group, roofColour, [0, facadeHeight + 0.1, 0], [5.5, 0.2, 3.65]);
        for (const x of [-2.6, 2.6])
          box(group, trim, [x, facadeHeight + 0.38, 0], [0.12, 0.55, 3.5]);
        box(group, trim, [0, facadeHeight + 0.38, 1.65], [5.2, 0.55, 0.12]);
        softBox(group, palette[2], [0.6, facadeHeight + 0.38, 0.5], [1.3, 0.45, 0.65]);
      } else shape(group, roof, roofColour, [0, facadeHeight - 0.05, 0], [5.8, 1.45, 4]);
      box(group, wall, [1.4, facadeHeight + 0.6, 0.65], [0.48, 1.4, 0.48]);
      softBox(group, trim, [1.4, facadeHeight + 1.28, 0.65], [0.65, 0.18, 0.65]);
      // Recessed doors and divided, framed windows read as architecture at a
      // glance; all these repeated solids are GPU-instanced by the scene owner.
      softBox(group, trim, [0, 0.88, -1.73], [0.93, 1.82, 0.16]);
      softBox(group, accent, [0, 0.8, -1.83], [0.69, 1.55, 0.065]);
      ball(group, '#e9c475', [0.21, 0.78, -1.87], [0.035, 0.035, 0.025]);
      for (const x of [-1.6, 1.6])
        for (let row = 0; row < 2; row++) {
          const y = 1.13 + (row * (facadeHeight - 1.5)) / 1.7;
          box(group, trim, [x, y, -1.755], [1.04, 1.34, 0.15]);
          box(
            group,
            neon ? '#61dfdb' : '#2d8fb5',
            [x, y, -1.845],
            [0.82, 1.12, 0.045],
            neon ? 0.32 : 0,
          );
          box(group, trim, [x, y, -1.88], [0.045, 1.12, 0.05]);
          box(group, trim, [x, y, -1.885], [0.82, 0.045, 0.05]);
          softBox(group, trim, [x, y - 0.7, -1.91], [1.16, 0.12, 0.32]);
          for (const side of [-1, 1])
            softBox(group, accent, [x + side * 0.65, y, -1.76], [0.2, 1.28, 0.09]);
          if (!neon && !frost && row === 0) {
            softBox(group, roofColour, [x, y - 0.68, -1.98], [0.94, 0.24, 0.33]);
            for (const offset of [-0.26, 0, 0.26]) {
              bead(group, '#379d78', [x + offset, y - 0.48, -1.98], [0.18, 0.16, 0.15]);
              bead(group, '#ffce63', [x + offset, y - 0.34, -2.03], [0.09, 0.08, 0.08]);
            }
          }
        }
      const awning = softBox(
        group,
        neon ? '#e375bb' : accent,
        [0, 2.15, -2.03],
        [1.45, 0.12, 0.74],
      );
      awning.rotation.x = 0.18;
      for (const x of [-0.5, 0, 0.5]) box(group, trim, [x, 2.14, -2.4], [0.2, 0.13, 0.08]);
      shape(group, cylinder, '#626d70', [2.35, 1.52, -2.42], [0.048, 3.04, 0.048]);
      box(group, '#666b70', [2.35, 3.05, -2.42], [0.3, 0.12, 0.3]);
      box(group, '#ffe7b0', [2.35, 3.25, -2.42], [0.23, 0.3, 0.23], neon ? 0.65 : 0.22);
      shape(group, cone, '#636970', [2.35, 3.49, -2.42], [0.24, 0.2, 0.24]);
      if (palace) {
        ball(group, '#a88ae0', [0, facadeHeight + 0.75, 0], [1.35, 0.9, 1.35]);
        shape(group, cone, '#efc35b', [0, facadeHeight + 1.8, 0], [0.16, 0.6, 0.16]);
        for (const x of [-2.25, 2.25])
          shape(
            group,
            cylinder,
            '#fff2cb',
            [x, facadeHeight / 2, -1.94],
            [0.16, facadeHeight, 0.16],
          );
      }
    } else if (theme === 'golden-desert' || theme === 'coral-beach') {
      ball(group, '#f8d28c', [0, -0.3, 0], [2.7, 0.6, 2.3]);
      if (theme === 'golden-desert') {
        shape(group, cylinder, '#32ad85', [0, 1.55, 0], [0.27, 3.1, 0.27]);
        ball(group, '#32ad85', [0, 3.1, 0], [0.27, 0.27, 0.27]);
        for (const side of [-1, 1]) {
          softBox(group, '#32ad85', [side * 0.45, 1.7, 0], [0.9, 0.35, 0.35]);
          shape(group, cylinder, '#32ad85', [side * 0.85, 2.15, 0], [0.18, 1, 0.18]);
          bead(group, '#f178b1', [side * 0.85, 2.72, 0], [0.25, 0.15, 0.25]);
        }
        bead(group, '#e99a60', [-1.2, 0.18, 0.2], [0.68, 0.32, 0.5]);
      } else {
        const trunk = shape(group, cylinder, '#ba744e', [0, 1.7, 0], [0.18, 3.4, 0.18]);
        trunk.rotation.z = 0.13;
        for (let i = 0; i < 5; i++) {
          const leaf = ball(group, i % 2 ? '#24b38e' : '#65cd8a', [0, 3.25, 0], [1.9, 0.13, 0.48]);
          leaf.rotation.y = (i * Math.PI) / 2.5;
          leaf.rotation.z = 0.13;
        }
        for (const x of [-0.22, 0.22]) bead(group, '#a45f44', [x, 3, -0.2], [0.22, 0.25, 0.2]);
        bead(group, '#ff8c91', [1.1, 0.12, -0.6], [0.45, 0.22, 0.32]);
      }
    } else if (theme === 'candy-kingdom' || theme === 'toy-workshop') {
      softBox(group, palette[1], [0, 0.35, 0], [2.2, 0.7, 2.1]);
      if (theme === 'candy-kingdom') {
        shape(group, cylinder, '#fff8eb', [0, 2, 0], [0.12, 3.3, 0.12]);
        const sweet = torus(group, '#ff62a9', [0, 3.1, 0], 0.95);
        sweet.scale.z = 2.7;
        ball(group, '#ffe38b', [0, 3.1, 0], [0.7, 0.7, 0.25]);
        for (const radius of [0.3, 0.57]) torus(group, '#fff6de', [0, 3.1, -0.24], radius);
        for (const y of [0.9, 1.3, 1.7, 2.1]) {
          const stripe = torus(group, '#ff62a9', [0, y, 0], 0.13);
          stripe.rotation.x = Math.PI / 2;
        }
      } else {
        softBox(group, '#33bed4', [-0.25, 1.3, 0], [1.6, 1.2, 1.6]);
        softBox(group, '#ffb941', [0.25, 2.4, 0], [1.5, 1, 1.5]);
        const roof = shape(group, cone, '#f05f94', [0.25, 3.4, 0], [1.2, 1, 1.2]);
        roof.rotation.y = Math.PI / 4;
        ball(group, '#fff4c2', [-0.25, 1.3, -0.82], [0.3, 0.3, 0.035]);
        for (const x of [-0.08, 0.58]) bead(group, '#fff4c2', [x, 2.4, -0.76], [0.1, 0.1, 0.025]);
      }
    } else if (
      theme === 'volcano-valley' ||
      theme === 'crystal-caverns' ||
      theme === 'aurora-ice'
    ) {
      const ice = theme === 'aurora-ice',
        volcano = theme === 'volcano-valley';
      for (let i = 0; i < 3; i++) {
        const colour = volcano
          ? '#694877'
          : ice
            ? ['#5edce5', '#8cb9ee', '#b1a0f2'][i]
            : ['#985de8', '#31c2c7', '#e078cb'][i];
        const peak = shape(
          group,
          volcano ? cone : prism,
          colour,
          [(i - 1) * 0.85, 1.6 + i * 0.25, i % 2],
          [1.15, 3.2 + i * 0.5, 1.15],
          volcano ? 0 : 0.04,
        );
        peak.rotation.z = (i - 1) * 0.13;
        if (!volcano) {
          const tip = shape(
            group,
            crystalTip,
            colour,
            [(i - 1) * 0.85, 3.6 + i * 0.5, i % 2],
            [1.15, 0.9, 1.15],
            0.04,
          );
          tip.rotation.z = peak.rotation.z;
          tip.position
            .copy(peak.position)
            .addScaledVector(
              new THREE.Vector3(-Math.sin(peak.rotation.z), Math.cos(peak.rotation.z), 0),
              peak.scale.y / 2 + tip.scale.y / 2,
            );
        }
      }
      if (volcano) {
        const lava = torus(group, '#ff9d44', [0, 2.8, 0], 0.48, 0.6);
        lava.rotation.x = Math.PI / 2;
        for (let i = 0; i < 3; i++)
          bead(
            group,
            '#ff9d44',
            [0.1 + i * 0.16, 2.2 - i * 0.6, -0.4 - i * 0.2],
            [0.22, 0.45, 0.12],
          );
      }
    } else if (theme === 'cloud-islands' || theme === 'starlight-space') {
      if (theme === 'cloud-islands') {
        for (let i = 0; i < 4; i++)
          ball(
            group,
            i % 2 ? '#e1efff' : '#fff5ed',
            [(i - 1.5) * 0.65, 0.1 + (i % 2) * 0.3, 0],
            [1.1, 0.6, 1],
          );
        ball(group, ['#e589d3', '#56c8d6', '#ffb466'][index % 3], [0, 3.2, 0], [1, 1.3, 1]);
        softBox(group, '#e9a258', [0, 1.25, 0], [0.65, 0.45, 0.65]);
        for (const x of [-0.28, 0.28])
          shape(group, cylinder, '#fff2d7', [x, 1.8, 0], [0.025, 0.8, 0.025]);
      } else {
        ball(group, palette[2], [0, 1.2, 0], [1.6, 1.6, 1.6]);
        const orbit = torus(group, '#ffd695', [0, 1.2, 0], 2.2, 0.25);
        orbit.rotation.x = 1.2;
        orbit.rotation.y = 0.3;
        bead(group, '#f5b8d6', [2.3, 2.65, 0.3], [0.45, 0.45, 0.45]);
      }
    } else if (theme === 'underwater-reef') {
      ball(group, '#758ccd', [0, -0.2, 0], [2, 0.5, 1.7]);
      for (let i = 0; i < 5; i++) {
        const coral = shape(
          group,
          cylinder,
          i % 2 ? '#f86f9d' : '#ffb457',
          [(i - 2) * 0.4, 1.1, (i % 2) * 0.3],
          [0.11, 1.8 + (i % 3) * 0.5, 0.11],
        );
        coral.rotation.z = (i - 2) * 0.13;
        bead(group, '#ffdf9f', [(i - 2) * 0.45, 2.1 + (i % 3) * 0.25, 0], [0.2, 0.2, 0.2]);
        for (const side of [-1, 1]) {
          const branch = shape(
            group,
            cylinder,
            i % 2 ? '#f86f9d' : '#ffb457',
            [(i - 2) * 0.4 + side * 0.23, 1.25, (i % 2) * 0.3],
            [0.075, 0.65, 0.075],
          );
          branch.rotation.z = -side * 0.7;
        }
      }
    } else if (theme === 'jungle-ruins' || theme === 'rainbow-waterfalls') {
      for (const x of [-1, 1]) softBox(group, '#78b49e', [x, 1.6, 0], [0.55, 3.2, 0.9]);
      softBox(group, '#f1cf8e', [0, 3.15, 0], [2.6, 0.5, 1]);
      if (theme === 'rainbow-waterfalls') {
        for (let i = 0; i < 4; i++)
          box(
            group,
            ['#50d6e4', '#76bfff', '#ab8bed', '#ef9bc9'][i],
            [(i - 1.5) * 0.36, 1.5, 0.05],
            [0.34, 2.8, 0.14],
            0.1,
          );
        for (const x of [-0.5, 0, 0.5]) bead(group, '#e2faff', [x, 0.15, -0.1], [0.48, 0.2, 0.35]);
      } else {
        for (let i = 0; i < 3; i++)
          ball(group, '#30aa78', [-1 + i, 3.6 - i * 0.2, 0], [0.85, 0.5, 0.65]);
        for (const x of [-1, 1]) box(group, '#e1b557', [x, 1.65, -0.47], [0.25, 0.8, 0.035]);
      }
    } else {
      const foliage =
        theme === 'cherry-garden'
          ? '#f878b5'
          : theme === 'autumn-woodland'
            ? '#f5a43d'
            : theme === 'moon-garden'
              ? '#a88ce8'
              : '#2db68d';
      shape(group, cylinder, '#a97159', [0, 1.2, 0], [0.2, 2.4, 0.2]);
      for (let i = 0; i < 3; i++)
        ball(group, foliage, [(i - 1) * 0.65, 2.6 + (i % 2) * 0.5, (i % 2) * 0.2], [1.2, 1.1, 1.1]);
      ball(group, theme === 'moon-garden' ? '#7b91ce' : '#8cc984', [0, -0.1, 0], [2.1, 0.3, 1.7]);
      for (const side of [-1, 1]) {
        const branch = shape(group, cylinder, '#a97159', [side * 0.42, 1.85, 0], [0.12, 1.4, 0.12]);
        branch.rotation.z = -side * 0.7;
        bead(
          group,
          theme === 'autumn-woodland' ? '#e26b51' : '#ffe4a1',
          [side * 1.15, 0.35, -0.65],
          [0.25, 0.22, 0.24],
        );
      }
    }
    return group;
  }
  function batchScenery(source) {
    source.updateMatrixWorld(true);
    const buckets = new Map();
    source.traverse((mesh) => {
      if (!mesh.isMesh) return;
      const key = `${mesh.geometry.uuid}:${mesh.material.uuid}`;
      if (!buckets.has(key))
        buckets.set(key, { geometry: mesh.geometry, material: mesh.material, matrices: [] });
      buckets.get(key).matrices.push(mesh.matrixWorld.clone());
    });
    const group = new THREE.Group();
    group.name = 'Instanced original course scenery';
    for (const { geometry, material, matrices } of buckets.values()) {
      const instances = keep(new THREE.InstancedMesh(geometry, material, matrices.length));
      matrices.forEach((matrix, i) => instances.setMatrixAt(i, matrix));
      instances.castShadow = true;
      instances.receiveShadow = true;
      instances.computeBoundingSphere();
      group.add(instances);
    }
    return group;
  }
  function dispose() {
    for (const resource of resources) resource.dispose();
    resources.clear();
    materials.clear();
  }
  return {
    keep,
    material,
    ball,
    box,
    torus,
    character,
    platform,
    food,
    hazard,
    finish,
    scenery,
    batchScenery,
    dispose,
  };
}
